import useSWRMutation from "swr/mutation"
import * as forge from "node-forge"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { invalidateIntegrationAuthorizations } from "features/data-management/integrations/useIntegrations"

interface TakCertificates {
    userCert: string
    ca?: string
    privateKey: string
    expires: number
}

async function extractTakCertificates(file: File, password: string): Promise<TakCertificates> {
    const arrayBuffer = await file.arrayBuffer()
    const p12Data = new Uint8Array(arrayBuffer)

    const binaryString = Array.from(p12Data)
        .map((byte) => String.fromCharCode(byte))
        .join("")

    const p12Asn1 = forge.asn1.fromDer(binaryString)
    let p12
    try {
        p12 = forge.pkcs12.pkcs12FromAsn1(p12Asn1, password)
    } catch (e) {
        console.error(e)
        throw { cause: "password", message: "Incorrect password" } as TAKAuthorizationError
    }

    // Extract certificates
    const certBags = p12.getBags({ bagType: forge.pki.oids.certBag })
    const certs = certBags[forge.pki.oids.certBag] || []

    // Extract private key
    const keyBags = p12.getBags({ bagType: forge.pki.oids.pkcs8ShroudedKeyBag })
    const keys = keyBags[forge.pki.oids.pkcs8ShroudedKeyBag] || []

    if (keys.length === 0 || !keys[0].key) {
        throw { cause: "p12", message: "No key found in p12 file" } as TAKAuthorizationError
    }

    if (certs.length === 0 || !certs[0].cert) {
        throw { cause: "p12", message: "No client certificate found in p12 file" }
    }

    // TAK typically has user cert first, then CA
    const userCertObj = certs[0].cert
    const userCert = forge.pki.certificateToPem(certs[0].cert)
    const ca = certs.length > 1 ? forge.pki.certificateToPem(certs.at(-1)!.cert!) : undefined
    const privateKey = forge.pki.privateKeyToPem(keys[0].key)
    const expires = userCertObj.validity.notAfter.valueOf()

    return {
        userCert,
        ca,
        privateKey,
        expires,
    }
}

interface AuthorizeTAKArgs {
    p12: File
    serverUrl: string
    workspaceId: string
    password: string
}

const exportData = async (
    _key: string,
    { arg: { p12, serverUrl, workspaceId, password } }: { arg: AuthorizeTAKArgs }
) => {
    const { ca, privateKey, userCert, expires } = await extractTakCertificates(p12, password)

    const res = await fetchApiGateway("post /org/:orgId/integrations/tak/authorize", {
        params: { orgId: workspaceId },
        body: { ca, key: privateKey, cert: userCert, serverUrl, expires },
        rawResponse: true,
    })

    if (!res.ok) {
        throw {
            cause: "network",
            message: "Failed to connect to TAK.",
        } as TAKAuthorizationError
    }
}

interface TAKAuthorizationError {
    message: string
    cause: string
}
const useAuthorizeTAK = (
    onSuccess: () => void,
    onError: (error: TAKAuthorizationError) => void
) => {
    const {
        trigger: authorizeTak,
        isMutating,
        error,
    } = useSWRMutation("tak-authorize", exportData, {
        onSuccess: () => {
            invalidateIntegrationAuthorizations()
            onSuccess()
        },
        onError,
        throwOnError: false,
    })

    return { authorizeTak, isMutating, error }
}

export { useAuthorizeTAK, extractTakCertificates }
export type { TAKAuthorizationError }
